import React, { useEffect, useState } from "react";
import backendApi from "../../../utils/BackendApi";
import { Container, IconButton } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

import "../../../../css/private/production-stats.css";

const COLORS = ["#4CAF50", "#FFC107", "#F44336"];
const PAGE_SIZE = 20;

function ProductionStats() {
  const [data, setData] = useState({
    kpi: { produzioniAttive: 0, completateOggi: 0, operaiAttivi: 0, efficienza: 0 },
    operaiAttivi: [],
    produzioneStato: [],
    operaiStats: [],
    timbrature: [],
    totalTimbrature: 0
  });

  const [pageNum, setPageNum] = useState(0);

  useEffect(() => {
    loadDashboard(pageNum);
  }, [pageNum]);

  const loadDashboard = async (page = 0) => {
    try {
      const resp = await backendApi.get("/api/production/dashboard", {
        params: { page, size: PAGE_SIZE }
      });

      if (!resp.data) {
        return;
      }

      // Popoliamo timbrature e totale
      setData({
        ...resp.data,
        timbrature: resp.data.timbrature || [],
        totalTimbrature: resp.data.timbratureTotal || 0
      });
    } catch (error) {
      console.error("Errore nel caricamento dashboard:", error);
    }
  };

  const handlePrev = () => {
    if (pageNum > 0) setPageNum(pageNum - 1);
  };

  const handleNext = () => {
    if ((pageNum + 1) * PAGE_SIZE < data.totalTimbrature) setPageNum(pageNum + 1);
  };

  return (
    <Container maxWidth={false}>
      <div className="production-dashboard">

        <h1>Production Dashboard</h1>

        {/* KPI */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <span>Produzioni attive</span>
            <h2>{data.kpi?.produzioniAttive}</h2>
          </div>
          <div className="kpi-card">
            <span>Completate oggi</span>
            <h2>{data.kpi?.completateOggi}</h2>
          </div>
          <div className="kpi-card">
            <span>Operai attivi</span>
            <h2>{data.kpi?.operaiAttivi}</h2>
          </div>
          <div className="kpi-card">
            <span>Efficienza</span>
            <h2>{data.kpi?.efficienza?.toFixed(2)}%</h2>
          </div>
        </div>

        {/* Operai attivi */}
        <div className="table-card">
          <h3>Operai attualmente al lavoro</h3>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Operaio</th>
                <th>Lotto</th>
                <th>Produzione</th>
                <th>Inizio lavoro</th>
                <th>Tempo attivo</th>
              </tr>
            </thead>
            <tbody>
              {data.operaiAttivi?.map((o, i) => (
                <tr key={i}>
                  <td>{o.matricola}</td>
                  <td>{o.lotto}</td>
                  <td>{o.produzione}</td>
                  <td>{o.start ? new Date(o.start).toLocaleString() : "-"}</td>
                  <td>{o.tempo || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Grafici */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3>Produzione per stato</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.produzioneStato}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                >
                  {data.produzioneStato?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Tempo lavorato per operaio</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.operaiStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="matricola" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="tempo" fill="#1976D2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Timbrature */}
        <div className="table-card">
          <h3>Ultime timbrature</h3>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Operaio</th>
                <th>Evento</th>
                <th>Motivo</th>
                <th>Produzione</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {data.timbrature?.map((t, i) => (
                <tr key={i}>
                  <td>{t.matricola}</td>
                  <td>{t.tipoEvento}</td>
                  <td>{t.motivoStop || "-"}</td>
                  <td>{t.produzione}</td>
                  <td>{t.timestamp ? new Date(t.timestamp).toLocaleString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginazione */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <IconButton onClick={handlePrev} disabled={pageNum === 0}>
              <ArrowBack />
            </IconButton>
            <span style={{ alignSelf: "center" }}>
              {pageNum * PAGE_SIZE + 1} - {Math.min((pageNum + 1) * PAGE_SIZE, data.totalTimbrature)} di {data.totalTimbrature}
            </span>
            <IconButton onClick={handleNext} disabled={(pageNum + 1) * PAGE_SIZE >= data.totalTimbrature}>
              <ArrowForward />
            </IconButton>
          </div>
        </div>

      </div>
    </Container>
  );
}

export default ProductionStats;
